import React, { useEffect, useState, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaFolder, FaDownload, FaEye, FaSearch, FaThLarge, FaList,
  FaTimes, FaRegFolderOpen, FaSchool, FaPhoneAlt, FaImages,
  FaArrowLeft, FaChevronLeft, FaChevronRight, FaFileArchive,
  FaUserGraduate, FaUsers, FaTrashAlt, FaSyncAlt, FaFileExcel,
  FaCheckSquare, FaSquare, FaCheck, FaFilePdf
} from "react-icons/fa";
import { getSchools } from "../../services/schoolService.jsx";
import {
  getFolders,
  deleteClassImage,
  deleteClassFolder,
  getSchoolExcelUrl,
  getTeacherExcelUrl,
  getSchoolAllImagesDownloadUrl,
  getClassFolderDownloadUrl,
} from "../../services/SchoolfileService.jsx";
import ClassPhotoPdfModal from "./ClassPhotoPdfModal.jsx";
import DeleteFolderModal from "./DeleteFolderModal.jsx";
import "./MangerLayer.css";

export const SchoolLayer = () => {
  // Navigation & View State
  const [view, setView] = useState("schools"); // schools | classes | students
  const [layout, setLayout] = useState("grid"); // grid | list

  // Data State
  const [schools, setSchools] = useState([]);
  const [folders, setFolders] = useState({});
  const [schoolInfo, setSchoolInfo] = useState({ name: "", phone: "", totalImages: 0, id: null });
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedClassNames, setSelectedClassNames] = useState([]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewingImage, setViewingImage] = useState(null);
  const itemsPerPage = layout === "grid" ? 12 : 10;

  // PDF Preview & Delete Folder Modals
  const [pdfModal, setPdfModal] = useState({
    isOpen: false,
    schoolName: "",
    className: "",
    students: [],
    allClassFolders: null,
  });

  const [deleteFolderModal, setDeleteFolderModal] = useState({
    isOpen: false,
    className: "",
    loading: false,
  });

  useEffect(() => { fetchInitialSchools(); }, []);

  const fetchInitialSchools = async () => {
    setLoading(true);
    try {
      const res = await getSchools();
      if (res?.data) setSchools(res.data);
      else if (Array.isArray(res)) setSchools(res);
    } catch { toast.error("Connection error. Please refresh."); }
    finally { setLoading(false); }
  };

  const handleSchoolClick = async (school) => {
    setLoading(true);
    setSearchTerm("");
    setCurrentPage(1);
    setSelectedClassNames([]);
    try {
      const res = await getFolders(school.id);
      if (res?.status) {
        setSchoolInfo({
          id: school.id,
          name: res.school_name || school.name,
          phone: res.phone || school.phone || "N/A",
          totalImages: res.data?.length || 0
        });

        const grouped = (res.data || []).reduce((acc, item) => {
          const cls = item.class_name || "General";
          if (!acc[cls]) acc[cls] = [];
          acc[cls].push(item);
          return acc;
        }, {});

        setFolders(grouped);
        setView("classes");
      } else {
        toast.error(res?.message || "Failed to load school folders");
      }
    } catch { toast.error("Failed to load school folders."); }
    finally { setLoading(false); }
  };

  // ==============================
  // DELETE IMAGE (Individual) – FIXED
  // ==============================
  // ==============================
  // DELETE IMAGE (Individual)
  // ==============================
  const handleDeleteImage = async (student) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the photo of student ${student.student_id}?`
    );

    if (!confirmDelete) return;

    let filePath = "";

    try {
      const url = new URL(student.photo);

      let pathname = url.pathname;

      if (pathname.startsWith("/uploads/")) {
        filePath = pathname.substring(9);
      } else if (pathname.startsWith("/api/uploads/")) {
        filePath = pathname.substring(13);
      } else {
        const segments = pathname.split("/").filter(Boolean);
        filePath = segments.slice(-4).join("/");
      }
    } catch (error) {
      filePath = student.photo;
    }

    // fallback
    if (!filePath || !filePath.includes("/")) {
      filePath = `school_${student.school_id}/teacher_${student.teacher_id}/class_${student.class_id}/${student.student_id}.jpg`;
    }

    console.log("Delete Request =>", {
      student_id: student.student_id,
      file: filePath,
    });

    try {
      const res = await deleteClassImage({
        student_id: student.student_id,
        file: filePath,
      });

      if (res?.status) {
        toast.success("Image deleted successfully");

        // stay on same page without redirect
        setFolders((prev) => {
          const updated = { ...prev };

          updated[selectedClass] = (updated[selectedClass] || []).filter(
            (item) => item.student_id !== student.student_id
          );

          return updated;
        });
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (error) {
      console.error("Delete image error:", error);
      toast.error("Delete failed");
    }
  };

  // ==============================
  // DELETE CLASS FOLDER WITH POPUP (Default "No")
  // ==============================
  const handleDeleteFolder = (className) => {
    // Open custom confirmation popup modal
    setDeleteFolderModal({
      isOpen: true,
      className,
      loading: false,
    });
  };

  const executeDeleteFolder = async () => {
    const className = deleteFolderModal.className;
    if (!className) return;

    setDeleteFolderModal((prev) => ({ ...prev, loading: true }));

    const classData = folders[className];
    if (!classData || classData.length === 0) {
      toast.error("No data for this class");
      setDeleteFolderModal({ isOpen: false, className: "", loading: false });
      return;
    }

    const firstItem = classData[0];
    console.log("🔍 First student object:", firstItem);

    // Extract IDs from download_folder URL
    let school_id, teacher_id, class_id;
    const downloadFolderUrl = firstItem.download_folder;
    if (downloadFolderUrl) {
      try {
        const url = new URL(downloadFolderUrl);
        school_id = url.searchParams.get("school_id");
        teacher_id = url.searchParams.get("teacher_id");
        class_id = url.searchParams.get("class_id");
      } catch (e) {
        console.error("Failed to parse download_folder URL", e);
      }
    }

    // Fallback: try to extract from photo URL if still missing
    if (!school_id || !teacher_id || !class_id) {
      const photoUrl = firstItem.photo;
      const match = photoUrl.match(/school_(\d+)\/teacher_(\d+)\/class_(\d+)/);
      if (match) {
        school_id = match[1];
        teacher_id = match[2];
        class_id = match[3];
      }
    }

    if (!school_id || !teacher_id || !class_id) {
      console.error("Missing IDs in student object:", { school_id, teacher_id, class_id });
      toast.error("Cannot delete folder – missing school/teacher/class IDs in data");
      setDeleteFolderModal({ isOpen: false, className: "", loading: false });
      return;
    }

    console.log("Deleting class folder with:", { school_id, teacher_id, class_id });

    try {
      const res = await deleteClassFolder(school_id, teacher_id, class_id);
      if (res?.status) {
        toast.success(`Class "${className}" deleted successfully`);
        const updatedFolders = { ...folders };
        delete updatedFolders[className];
        setFolders(updatedFolders);
        if (Object.keys(updatedFolders).length === 0) {
          setView("schools");
          setFolders({});
        }
      } else {
        toast.error(res?.message || "Folder delete failed");
      }
    } catch (error) {
      console.error("Delete folder error:", error);
      toast.error("Folder delete failed");
    } finally {
      setDeleteFolderModal({ isOpen: false, className: "", loading: false });
    }
  };

  // ==============================
  // UI DOWNLOAD (A4 PHOTO PDF) HANDLERS
  // ==============================
  // Open A4 Photo PDF for single class
  const handleOpenClassPdf = (className) => {
    const classData = folders[className] || [];
    if (classData.length === 0) {
      toast.warn(`No student photos in Class ${className}`);
      return;
    }
    setPdfModal({
      isOpen: true,
      schoolName: schoolInfo.name || "School",
      className: className,
      students: classData,
      allClassFolders: null,
    });
  };

  // Open A4 Photo PDF for all classes of currently opened school
  const handleOpenCurrentSchoolAllPdf = () => {
    const classNames = Object.keys(folders);
    if (classNames.length === 0) {
      toast.warn("No classes found to download");
      return;
    }
    const firstClass = classNames[0];
    setPdfModal({
      isOpen: true,
      schoolName: schoolInfo.name || "School",
      className: firstClass,
      students: folders[firstClass] || [],
      allClassFolders: folders,
    });
  };

  // Open A4 Photo PDF for school from School Directory
  const handleOpenSchoolPdf = async (school) => {
    setLoading(true);
    try {
      const res = await getFolders(school.id);
      if (res?.status && res.data && res.data.length > 0) {
        const grouped = res.data.reduce((acc, item) => {
          const cls = item.class_name || "General";
          if (!acc[cls]) acc[cls] = [];
          acc[cls].push(item);
          return acc;
        }, {});

        const classNames = Object.keys(grouped);
        const firstClass = classNames[0] || "";
        setPdfModal({
          isOpen: true,
          schoolName: res.school_name || school.name,
          className: firstClass,
          students: grouped[firstClass] || [],
          allClassFolders: grouped,
        });
      } else {
        toast.warn(`No student photos uploaded for ${school.name}`);
      }
    } catch (error) {
      console.error("Failed to load school folders for PDF:", error);
      toast.error("Failed to load school photos");
    } finally {
      setLoading(false);
    }
  };

  // Helper to format real photo number: 3 digits, space, 3 digits (e.g. "102 830", "123 456")
  const formatPhotoNumber = (st) => {
    if (!st) return "-";
    let raw = "";
    if (st.photo_no) {
      raw = String(st.photo_no);
    } else if (st.photo_number) {
      raw = String(st.photo_number);
    } else if (st.student_id) {
      const parts = String(st.student_id).split("-");
      if (parts.length > 1 && parts[1]) {
        raw = parts[1];
      } else {
        raw = String(st.student_id);
      }
    } else if (st.photo) {
      const m = String(st.photo).match(/photo_[^\-_]+-([^_.]+)/);
      if (m && m[1]) raw = m[1];
    }

    const str = String(raw).trim();
    if (!str) return "-";

    const digits = str.replace(/\D/g, "");
    if (digits.length === 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    }
    if (digits.length > 6) {
      const last6 = digits.slice(-6);
      return `${last6.slice(0, 3)} ${last6.slice(3)}`;
    }
    return str.replace(/-/g, " ");
  };

  // Helper to format student ID (e.g. 152153-993328)
  const formatStudentId = (id) => {
    if (!id) return "-";
    return String(id);
  };

  // Helper to get Class ID from class data
  const getClassId = (className) => {
    const classData = folders[className];
    if (!classData || classData.length === 0) return null;
    const firstItem = classData[0];
    if (firstItem.class_id) return firstItem.class_id;
    if (firstItem.download_folder) {
      try {
        const url = new URL(firstItem.download_folder);
        const cId = url.searchParams.get("class_id");
        if (cId) return cId;
      } catch (e) { }
    }
    if (firstItem.photo) {
      const match = firstItem.photo.match(/class_(\d+)/);
      if (match) return match[1];
    }
    return null;
  };

  // Reliable File / ZIP Trigger Helper
  const triggerDownloadUrl = (url, fileName = "download.zip") => {
    try {
      const link = document.createElement("a");
      link.href = url;
      if (fileName) link.setAttribute("download", fileName);
      link.setAttribute("target", "_blank");
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
      }, 150);
    } catch (e) {
      window.open(url, "_blank");
    }
  };

  // Download All Images for School (ZIP)
  const handleDownloadAllImages = (schoolId, schoolName = "") => {
    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }
    toast.info(`Preparing full ZIP download of all images for ${schoolName || "school"}...`);
    const url = getSchoolAllImagesDownloadUrl(schoolId);
    triggerDownloadUrl(url, `${schoolName || "School"}_All_Images.zip`);
  };

  // Download Single Class Folder ZIP
  const handleFolderDownload = async (className) => {
    const classData = folders[className];
    if (!classData || classData.length === 0) {
      toast.error("No data available for download");
      return;
    }

    const sId = schoolInfo.id || classData[0]?.school_id;
    const classId = getClassId(className) || classData[0]?.class_id;
    const schoolName = schoolInfo.name || "School";
    const fileName = `${schoolName}_${className}.zip`;

    const zipUrl = classData[0]?.download_folder;
    if (zipUrl) {
      toast.info(`Downloading ${fileName}...`);
      triggerDownloadUrl(zipUrl, fileName);
      return;
    }

    if (sId && classId) {
      toast.info(`Preparing ${fileName} for download...`);
      const url = getClassFolderDownloadUrl(sId, classId);
      triggerDownloadUrl(url, fileName);
      return;
    }

    toast.error("Download link not available for this class folder");
  };

  // Multi-select toggle for classes
  const handleToggleSelectClass = (className, e) => {
    if (e) e.stopPropagation();
    setSelectedClassNames((prev) =>
      prev.includes(className)
        ? prev.filter((c) => c !== className)
        : [...prev, className]
    );
  };

  // Select all classes in current view
  const handleSelectAllClasses = () => {
    if (selectedClassNames.length === filteredItems.length && filteredItems.length > 0) {
      setSelectedClassNames([]);
    } else {
      setSelectedClassNames([...filteredItems]);
    }
  };

  // Download Multiple Selected Classes ZIP
  const handleDownloadSelectedClasses = () => {
    if (selectedClassNames.length === 0) {
      toast.warn("Please select at least one class to download");
      return;
    }
    const classIds = selectedClassNames
      .map((cName) => getClassId(cName))
      .filter(Boolean);

    if (classIds.length === 0) {
      toast.error("Could not find class IDs for selected classes");
      return;
    }

    const schoolName = schoolInfo.name || "School";
    toast.info(`Preparing ZIP download for ${classIds.length} classes...`);
    const url = getClassFolderDownloadUrl(schoolInfo.id, classIds);
    triggerDownloadUrl(url, `${schoolName}_Selected_${classIds.length}_Classes.zip`);
  };

  // Download Individual File
  const handleFileDownload = (url, studentId) => {
    toast.info(`Downloading ${studentId}.jpg...`);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${studentId}.jpg`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // View Image in Modal
  const handleImageView = (url) => {
    setViewingImage(url);
  };

  // Export School Uploaded Data Excel
  const handleExportSchoolExcel = (schoolId, schoolName = "") => {
    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }
    toast.info(`Exporting School Excel${schoolName ? ` for ${schoolName}` : ""}...`);
    const url = getSchoolExcelUrl(schoolId);
    window.open(url, "_blank");
  };

  // Export Teacher / App Uploaded Data Excel
  const handleExportTeacherExcel = (schoolId, schoolName = "") => {
    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }
    toast.info(`Exporting App / Teacher Excel${schoolName ? ` for ${schoolName}` : ""}...`);
    const url = getTeacherExcelUrl(schoolId);
    window.open(url, "_blank");
  };

  // --- Search & Filtering Logic ---
  const filteredItems = useMemo(() => {
    if (view === "schools") {
      return schools.filter(s =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.phone && s.phone.includes(searchTerm))
      );
    }
    if (view === "classes") {
      return Object.keys(folders).filter(cls =>
        cls.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (view === "students") {
      return (folders[selectedClass] || []).filter(st => {
        const term = searchTerm.toLowerCase();
        const cleanTerm = term.replace(/[\s-]/g, "");
        const pNo = formatPhotoNumber(st).toLowerCase();
        const cleanPNo = pNo.replace(/[\s-]/g, "");
        const sId = (st.student_id || "").toLowerCase();
        const cleanSId = sId.replace(/[\s-]/g, "");
        return (
          sId.includes(term) ||
          cleanSId.includes(cleanTerm) ||
          pNo.includes(term) ||
          cleanPNo.includes(cleanTerm) ||
          (st.teacher_name && st.teacher_name.toLowerCase().includes(term))
        );
      });
    }
    return [];
  }, [view, schools, folders, selectedClass, searchTerm]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, view]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedData = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Calculate stats
  const totalClasses = Object.keys(folders).length;
  const currentClassStudentCount = selectedClass ? (folders[selectedClass]?.length || 0) : 0;

  return (
    <div className="premium-manager">
      <ToastContainer position="top-right" autoClose={1500} theme="colored" />

      {/* Image Viewer Modal */}
      {viewingImage && (
        <div className="image-modal" onClick={() => setViewingImage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setViewingImage(null)}>
              <FaTimes />
            </button>
            <img src={viewingImage} alt="Preview" />
            <div className="modal-actions">
              <button onClick={() => window.open(viewingImage, "_blank")}>
                <FaEye /> Open in New Tab
              </button>
              <button
                className="download-folder-btn"
                onClick={() => handleFolderDownload(selectedClass)}
              >
                <FaFileArchive /> Download Class Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- SMART HEADER --- */}
      <header className="premium-header">
        <div className="header-top">
          <div className="breadcrumb-area">
            {view !== "schools" && (
              <button className="icon-btn-back" onClick={() => {
                if (view === "students") {
                  setView("classes");
                  setSelectedClass("");
                } else {
                  setView("schools");
                  setFolders({});
                  setSelectedClassNames([]);
                }
                setSearchTerm("");
                setCurrentPage(1);
              }}>
                <FaArrowLeft />
              </button>
            )}
            <div className="brand">
              <FaRegFolderOpen className="brand-icon" />
              <div>
                <h5>
                  {view === "schools" ? "School Directory" : schoolInfo.name}
                </h5>
                <p>
                  {view === "students" ? `Class ${selectedClass}` : "Cloud File Management"}
                </p>
              </div>
            </div>
          </div>

          <div className="header-actions">
            <div className="search-bar-wrapper">
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={
                  view === "schools" ? "Search schools..." :
                    view === "classes" ? "Search classes..." :
                      "Search student ID or teacher..."
                }
              />
              {searchTerm && (
                <FaTimes onClick={() => setSearchTerm("")} className="clear-btn" />
              )}
            </div>

            <div className="view-toggle">
              <button
                className={layout === "grid" ? "active" : ""}
                onClick={() => setLayout("grid")}
                title="Grid View"
              >
                <FaThLarge />
              </button>

              <button
                className={layout === "list" ? "active" : ""}
                onClick={() => setLayout("list")}
                title="List View"
              >
                <FaList />
              </button>

              <button
                className="refresh-btn"
                onClick={async () => {
                  if (view === "schools") {
                    await fetchInitialSchools();
                  }

                  if (view === "classes") {
                    const school = schools.find((s) => s.id === schoolInfo.id);

                    if (school) {
                      await handleSchoolClick(school);
                      setView("classes");
                    }
                  }

                  if (view === "students") {
                    const school = schools.find((s) => s.id === schoolInfo.id);

                    if (school) {
                      await handleSchoolClick(school);

                      setSelectedClass(selectedClass);
                      setView("students");
                    }
                  }

                  toast.success("Data refreshed");
                }}
                title="Refresh Data"
              >
                <FaSyncAlt />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        {view === "classes" && (
          <div className="stats-bar">
            <div className="stat-item">
              <FaSchool />
              <span>Classes: {totalClasses}</span>
            </div>
            <div className="stat-item">
              <FaImages />
              <span>Total Images: {schoolInfo.totalImages}</span>
            </div>
            <div className="stat-item">
              <FaPhoneAlt />
              <span>{schoolInfo.phone}</span>
            </div>
            <div className="stats-export-actions">
              <button
                className="export-btn all-download-btn"
                onClick={() => handleDownloadAllImages(schoolInfo.id, schoolInfo.name)}
                title="Download All Images For School (ZIP)"
              >
                <FaFileArchive /> Download All (ZIP)
              </button>
              {selectedClassNames.length > 0 && (
                <button
                  className="export-btn selected-download-btn"
                  onClick={handleDownloadSelectedClasses}
                  title={`Download Selected ${selectedClassNames.length} Classes (ZIP)`}
                >
                  <FaDownload /> Download Selected ({selectedClassNames.length})
                </button>
              )}
              <button
                className="export-btn school-export-btn"
                onClick={() => handleExportSchoolExcel(schoolInfo.id, schoolInfo.name)}
                title="Export School Uploaded List (Excel)"
              >
                <FaFileExcel /> School List Export
              </button>
              <button
                className="export-btn app-export-btn"
                onClick={() => handleExportTeacherExcel(schoolInfo.id, schoolInfo.name)}
                title="Export App/Teacher Uploaded List (Excel)"
              >
                <FaFileExcel /> App List Export
              </button>
            </div>
          </div>
        )}

        {view === "students" && (
          <div className="stats-bar">
            <div className="stat-item">
              <FaSchool />
              <span>{schoolInfo.name}</span>
            </div>
            <div className="stat-item">
              <FaUserGraduate />
              <span>Class: {selectedClass}</span>
            </div>
            <div className="stat-item">
              <FaUsers />
              <span>Students: {currentClassStudentCount}</span>
            </div>
            <div className="stats-export-actions">
              <button
                className="export-btn school-export-btn"
                onClick={() => handleExportSchoolExcel(schoolInfo.id, schoolInfo.name)}
                title="Export School Uploaded List (Excel)"
              >
                <FaFileExcel /> School List Export
              </button>
              <button
                className="export-btn app-export-btn"
                onClick={() => handleExportTeacherExcel(schoolInfo.id, schoolInfo.name)}
                title="Export App/Teacher Uploaded List (Excel)"
              >
                <FaFileExcel /> App List Export
              </button>
              <button
                className="export-btn ui-download-btn"
                onClick={() => handleOpenClassPdf(selectedClass)}
                title={`PDF Download ${selectedClass} (A4 Photo Sheet)`}
              >
                <FaFilePdf /> PDF Download
              </button>
              <button
                className="download-folder-btn"
                onClick={() => handleFolderDownload(selectedClass)}
              >
                <FaFileArchive /> Download Class Folder
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="content-container">
        {loading ? (
          <div className="loader-box">
            <div className="pulse-spinner"></div>
            <p>Syncing Data...</p>
          </div>
        ) : (
          <>
            {/* Empty State */}
            {filteredItems.length === 0 && (
              <div className="empty-state">
                <FaFolder className="empty-icon" />
                <h3>No {view} found</h3>
                <p>{searchTerm ? "Try adjusting your search" : "Add some data to get started"}</p>
              </div>
            )}

            {/* Selection Toolbar for Classes View */}
            {view === "classes" && filteredItems.length > 0 && (
              <div className="classes-selection-bar">
                <button
                  className={`select-all-btn ${selectedClassNames.length === filteredItems.length && filteredItems.length > 0 ? "active" : ""}`}
                  onClick={handleSelectAllClasses}
                >
                  {selectedClassNames.length === filteredItems.length && filteredItems.length > 0 ? (
                    <><FaCheckSquare className="checkbox-icon checked" /> Deselect All ({filteredItems.length})</>
                  ) : (
                    <><FaSquare className="checkbox-icon" /> Select All ({filteredItems.length})</>
                  )}
                </button>
                {selectedClassNames.length > 0 && (
                  <div className="selection-info-group">
                    <span className="selected-count-badge">
                      {selectedClassNames.length} of {filteredItems.length} selected
                    </span>
                    <button
                      className="export-btn selected-download-btn"
                      onClick={handleDownloadSelectedClasses}
                    >
                      <FaDownload /> Download Selected ({selectedClassNames.length})
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* GRID VIEW (For Schools & Classes) */}
            {(view === "schools" || view === "classes") && layout === "grid" && filteredItems.length > 0 && (
              <div className="smart-grid">
                {paginatedData.map((item) => (
                  <div
                    key={view === "schools" ? item.id : item}
                    className={`glass-card ripple ${view === "classes" && selectedClassNames.includes(item) ? "card-selected" : ""}`}
                    onClick={() => {
                      if (view === "schools") {
                        handleSchoolClick(item);
                      } else {
                        setSelectedClass(item);
                        setView("students");
                        setSearchTerm("");
                        setCurrentPage(1);
                      }
                    }}
                  >
                    {view === "classes" && (
                      <div
                        className={`class-card-checkbox ${selectedClassNames.includes(item) ? "checked" : ""}`}
                        onClick={(e) => handleToggleSelectClass(item, e)}
                        title={selectedClassNames.includes(item) ? "Deselect class" : "Select class for multi-download"}
                      >
                        {selectedClassNames.includes(item) && <FaCheck className="checkbox-tick-icon" />}
                      </div>
                    )}

                    <div className="card-body">
                      <div className="folder-stack">
                        <FaFolder className="folder-main" />
                        {view === "classes" && (
                          <span className="folder-badge">{folders[item].length}</span>
                        )}
                      </div>
                      <div className="card-text">
                        <h5>{view === "schools" ? item.name : (item.toLowerCase().startsWith("class") ? item : `Class ${item}`)}</h5>
                        <p>
                          {view === "schools" ? (
                            <><FaPhoneAlt className="card-icon" /> {item.phone || "No Contact"}</>
                          ) : (
                            <><FaUsers className="card-icon" /> {folders[item].length} Students</>
                          )}
                        </p>
                      </div>
                      {view === "schools" && (
                        <div className="school-card-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="export-btn all-download-btn"
                            onClick={() => handleDownloadAllImages(item.id, item.name)}
                            title="Download All Images For School (ZIP)"
                          >
                            <FaFileArchive /> Download All (ZIP)
                          </button>
                          <button
                            className="export-btn school-export-btn"
                            onClick={() => handleExportSchoolExcel(item.id, item.name)}
                            title="Export School Uploaded List (Excel)"
                          >
                            <FaFileExcel /> School List Export
                          </button>
                          <button
                            className="export-btn app-export-btn"
                            onClick={() => handleExportTeacherExcel(item.id, item.name)}
                            title="Export App/Teacher Uploaded List (Excel)"
                          >
                            <FaFileExcel /> App List Export
                          </button>
                        </div>
                      )}
                      {view === "classes" && (
                        <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="card-ui-download-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenClassPdf(item);
                            }}
                            title={`PDF Download ${item} (A4 Photo Sheet)`}
                          >
                            <FaFilePdf /> PDF Download
                          </button>
                          <button
                            className="card-download-btn visible-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFolderDownload(item);
                            }}
                            title={`Download ${item} Folder (ZIP)`}
                          >
                            <FaDownload /> Download ZIP
                          </button>
                          <button
                            className="card-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(item);
                            }}
                            title="Delete Class Folder"
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* LIST VIEW (For Schools & Classes) */}
            {(view === "schools" || view === "classes") && layout === "list" && filteredItems.length > 0 && (
              <div className="smart-list">
                {paginatedData.map((item) => (
                  <div
                    key={view === "schools" ? item.id : item}
                    className={`list-item ${view === "classes" && selectedClassNames.includes(item) ? "item-selected" : ""}`}
                    onClick={() => {
                      if (view === "schools") {
                        handleSchoolClick(item);
                      } else {
                        setSelectedClass(item);
                        setView("students");
                        setSearchTerm("");
                        setCurrentPage(1);
                      }
                    }}
                  >
                    <div className="list-item-left">
                      {view === "classes" && (
                        <div
                          className={`list-checkbox-wrapper ${selectedClassNames.includes(item) ? "checked" : ""}`}
                          onClick={(e) => handleToggleSelectClass(item, e)}
                          title={selectedClassNames.includes(item) ? "Deselect class" : "Select class for multi-download"}
                        >
                          {selectedClassNames.includes(item) && <FaCheck className="checkbox-tick-icon" />}
                        </div>
                      )}
                      <FaFolder className="list-folder-icon" />
                      <div className="list-info">
                        <h5>{view === "schools" ? item.name : (item.toLowerCase().startsWith("class") ? item : `Class ${item}`)}</h5>
                        <span>
                          {view === "schools" ? (
                            <><FaPhoneAlt /> {item.phone || "No Contact"}</>
                          ) : (
                            <><FaUsers /> {folders[item].length} Students</>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="list-item-right">
                      {view === "schools" && (
                        <div className="school-list-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="export-btn all-download-btn"
                            onClick={() => handleDownloadAllImages(item.id, item.name)}
                            title="Download All Images For School (ZIP)"
                          >
                            <FaFileArchive /> Download All (ZIP)
                          </button>
                          <button
                            className="export-btn school-export-btn"
                            onClick={() => handleExportSchoolExcel(item.id, item.name)}
                            title="Export School Uploaded List (Excel)"
                          >
                            <FaFileExcel /> School List Export
                          </button>
                          <button
                            className="export-btn app-export-btn"
                            onClick={() => handleExportTeacherExcel(item.id, item.name)}
                            title="Export App/Teacher Uploaded List (Excel)"
                          >
                            <FaFileExcel /> App List Export
                          </button>
                        </div>
                      )}
                      {view === "classes" && (
                        <>
                          <button
                            className="list-ui-download-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenClassPdf(item);
                            }}
                            title={`PDF Download ${item} (A4 Photo Sheet)`}
                          >
                            <FaFilePdf /> PDF Download
                          </button>
                          <button
                            className="list-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(item);
                            }}
                            title="Delete Class Folder"
                          >
                            <FaTrashAlt />
                          </button>
                          <button
                            className="list-download-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFolderDownload(item);
                            }}
                            title="Download Class Folder (ZIP)"
                          >
                            <FaDownload /> Download
                          </button>
                        </>
                      )}
                      <FaChevronRight className="arrow-end" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* STUDENT TABLE VIEW */}
            {view === "students" && filteredItems.length > 0 && (
              <div className="table-wrapper glass-effect">
                <div className="table-responsive">
                  <table className="pro-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Photo No</th>
                        <th>Student ID</th>
                        <th>Class Teacher</th>
                        <th>Preview</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((st, idx) => (
                        <tr key={st.student_id || idx} className="fade-in">
                          <td className="serial-cell">
                            {(currentPage - 1) * itemsPerPage + idx + 1}
                          </td>
                          <td>
                            <span className="photo-no-badge">
                              {formatPhotoNumber(st)}
                            </span>
                          </td>
                          <td>
                            <span className="id-badge">
                              {formatStudentId(st.student_id)}
                            </span>
                          </td>
                          <td>{st.teacher_name || "N/A"}</td>
                          <td>
                            <div className="table-avatar">
                              <img
                                src={st.photo}
                                alt={st.student_id}
                                onError={(e) => e.target.src = "https://via.placeholder.com/60?text=No+Image"}
                              />
                              <div className="avatar-hover" onClick={() => handleImageView(st.photo)}>
                                <FaEye />
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="action-group">
                              <button
                                onClick={() => handleImageView(st.photo)}
                                className="action-btn view-btn"
                                title="View Image"
                              >
                                <FaEye />
                              </button>
                              <button
                                onClick={() => handleFileDownload(st.download_photo, st.student_id)}
                                className="action-btn download-btn"
                                title="Download Image"
                              >
                                <FaDownload />
                              </button>
                              <button
                                onClick={() => handleDeleteImage(st)}
                                className="action-btn delete-btn"
                                title="Delete Image"
                              >
                                <FaTrashAlt />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* SHARED PAGINATION */}
            {totalPages > 1 && (
              <div className="pro-pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="page-nav"
                >
                  <FaChevronLeft />
                </button>

                <div className="page-info">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="page-ellipsis">...</span>
                      <button
                        className="page-number"
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="page-nav"
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Photo Sheet PDF Preview Modal (30 Photos Per Page: 6 columns x 5 rows) */}
      <ClassPhotoPdfModal
        isOpen={pdfModal.isOpen}
        onClose={() => setPdfModal((prev) => ({ ...prev, isOpen: false }))}
        schoolName={pdfModal.schoolName}
        className={pdfModal.className}
        students={pdfModal.students}
        allClassFolders={pdfModal.allClassFolders}
      />

      {/* Delete Folder Confirmation Modal (Default "No" Ticked) */}
      <DeleteFolderModal
        isOpen={deleteFolderModal.isOpen}
        onClose={() => setDeleteFolderModal({ isOpen: false, className: "", loading: false })}
        onConfirm={executeDeleteFolder}
        folderName={deleteFolderModal.className}
        itemType="Class Folder"
        loading={deleteFolderModal.loading}
      />
    </div>
  );
};